import React from 'react';

// When adding a new article:
//   1. Create the .mdx file in src/content/blog/
//   2. Add an import and an entry to ARTICLES below
//   3. scripts/seo-routes.js auto-discovers .mdx files from the filesystem,
//      so the sitemap + prerender list update without further changes.
import * as ReactI18nextTutorial from '../../content/blog/react-i18next-tutorial.mdx';
import * as NextjsInternationalizationGuide from '../../content/blog/nextjs-internationalization-guide.mdx';
import * as AiVsHumanTranslationSoftware from '../../content/blog/ai-vs-human-translation-software.mdx';
import * as I18nCicdGithubActions from '../../content/blog/i18n-cicd-github-actions.mdx';
import * as BestI18nToolsForDevelopers2026 from '../../content/blog/best-i18n-tools-for-developers-2026.mdx';
import * as HelloWorld from '../../content/blog/hello-world.mdx';

interface ArticleFrontmatter {
  title: string;
  /**
   * Publication date, `YYYY-MM-DD` or a full ISO timestamp.
   *
   * Prefer a timestamp when more than one article ships on the same day: a
   * date-only value cannot order same-day posts, and the list then falls back to
   * the declaration order below, which is editorial rather than chronological.
   */
  date: string;
  description: string;
  author: string;
  tags?: string[];
  ogImage?: string;
  draft?: boolean;
}

interface ArticleModule {
  default: React.ComponentType;
  frontmatter: ArticleFrontmatter;
}

export interface ArticleEntry {
  slug: string;
  module: ArticleModule;
}

/**
 * Declaration order is the editorial tie-break.
 *
 * Articles are listed newest first by date, and every published article currently
 * has a distinct one, so this order does not decide anything today. It only matters
 * if two articles ever share a date: then the list is read top-to-bottom as "which
 * of these should a reader see first". Give same-day articles ISO timestamps rather
 * than relying on it.
 */
const ARTICLES: ArticleEntry[] = [
  { slug: 'react-i18next-tutorial', module: ReactI18nextTutorial as unknown as ArticleModule },
  { slug: 'nextjs-internationalization-guide', module: NextjsInternationalizationGuide as unknown as ArticleModule },
  { slug: 'ai-vs-human-translation-software', module: AiVsHumanTranslationSoftware as unknown as ArticleModule },
  { slug: 'i18n-cicd-github-actions', module: I18nCicdGithubActions as unknown as ArticleModule },
  { slug: 'best-i18n-tools-for-developers-2026', module: BestI18nToolsForDevelopers2026 as unknown as ArticleModule },
  { slug: 'hello-world', module: HelloWorld as unknown as ArticleModule },
];

let cached: ArticleEntry[] | null = null;

/**
 * Milliseconds for a frontmatter date, or null if it cannot be read.
 *
 * Parsed rather than string-compared. `localeCompare` only orders ISO dates by
 * accident — it agrees with chronology for `YYYY-MM-DD`, but ranks `2026-5-9`
 * after `2026-05-29` and cannot compare a date against a timestamp at all, both of
 * which the frontmatter now permits.
 */
function publishedAt(entry: ArticleEntry): number | null {
  const raw = entry.module.frontmatter?.date;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Published articles, newest first, ties broken by declaration order.
 *
 * `sort` is stable in every engine this ships to, so returning 0 for equal dates
 * preserves the order in ARTICLES — which is the point, and is why that list is
 * documented as editorial. Undated articles sort last rather than being dropped: a
 * missing date is a content bug, and hiding the article is a worse way to find out.
 */
export function sortArticles(entries: ArticleEntry[]): ArticleEntry[] {
  return entries.slice().sort((a, b) => {
    const aAt = publishedAt(a);
    const bAt = publishedAt(b);
    if (aAt === null && bAt === null) return 0;
    if (aAt === null) return 1;
    if (bAt === null) return -1;
    return bAt - aAt;
  });
}

export function listArticles(): ArticleEntry[] {
  if (cached) return cached;
  const published = ARTICLES.filter(e => e.module.frontmatter && !e.module.frontmatter.draft);
  cached = sortArticles(published);

  if (process.env.NODE_ENV === 'development') {
    const byDate = new Map<number, string[]>();
    for (const entry of cached) {
      const at = publishedAt(entry);
      if (at === null) continue;
      byDate.set(at, [...(byDate.get(at) ?? []), entry.slug]);
    }
    // Array.from rather than iterating the Map directly: the build targets an older
    // ES level and downlevelIteration is off.
    for (const [at, slugs] of Array.from(byDate.entries())) {
      if (slugs.length < 2) continue;
      // eslint-disable-next-line no-console
      console.warn(
        `[blog] ${slugs.length} articles share the date ${new Date(at).toISOString().slice(0, 10)}, ` +
          'so their order comes from the ARTICLES declaration order rather than from ' +
          `chronology: ${slugs.join(', ')}. Give them timestamps to order them properly.`
      );
    }
  }

  return cached;
}

/**
 * Whether the newest article is unambiguously newest.
 *
 * The index badges the first card "Latest", which is only true if nothing else
 * shares its date. With four articles on one day that badge would otherwise be
 * decided by declaration order and would be quietly wrong.
 */
export function hasDistinctLatest(entries: ArticleEntry[] = listArticles()): boolean {
  if (entries.length < 2) return entries.length === 1;
  const first = publishedAt(entries[0]);
  const second = publishedAt(entries[1]);
  return first !== null && (second === null || first > second);
}

export function findArticle(slug: string): ArticleEntry | undefined {
  return listArticles().find(a => a.slug === slug);
}
